import React, { useEffect, useState } from "react";
import { Video, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const AdminHelpVideos = () => {
  const [v, setV] = useState([]);
  useEffect(() => { api.get("/help/videos").then((r) => setV(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/help/videos", { urls: Object.fromEntries(v.map((x) => [x.key, x.url])) }); toast.success("Liens des vidéos enregistrés"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6 space-y-3" data-testid="admin-help-videos">
      <h3 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Video className="w-4 h-4" /> Vidéos d'aide (menu Aide)</h3>
      <p className="text-xs text-brand-green/50">Collez un lien https (YouTube, Vimeo…). Laissé vide, la vidéo s'affiche « bientôt ».</p>
      {v.map((x, i) => (
        <label key={x.key} className="grid sm:grid-cols-[180px_1fr] gap-2 items-center text-sm">{x.title}
          <input value={x.url} onChange={(e) => setV(v.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)))} placeholder="https://youtu.be/…" className="input" data-testid={`admin-video-${x.key}`} />
        </label>
      ))}
      <button onClick={save} className="px-4 py-2 rounded-full bg-brand-lime text-brand-greenDeep text-sm font-semibold" data-testid="admin-videos-save">Enregistrer</button>
    </div>
  );
};

export const AdminEmailTest = () => {
  const [to, setTo] = useState("");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setR(null);
    try { setR((await api.post("/admin/email-test", { to })).data); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6 space-y-3" data-testid="admin-email-test">
      <h3 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Mail className="w-4 h-4" /> Tester l'envoi d'e-mail</h3>
      <p className="text-xs text-brand-green/50">Envoie un e-mail de test et affiche la cause exacte en cas d'échec. Laissé vide : votre adresse.</p>
      <div className="flex flex-wrap gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinataire@exemple.fr" className="input flex-1 min-w-[220px]" data-testid="admin-email-test-to" />
        <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime text-brand-greenDeep text-sm font-semibold disabled:opacity-50" data-testid="admin-email-test-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer un test
        </button>
      </div>
      {r && (
        <div className={`rounded-xl p-4 text-sm space-y-1 ${r.sent ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`} data-testid="admin-email-test-result">
          <p className="font-semibold" data-testid="admin-email-test-cause">{r.sent ? "Envoi réussi" : `Échec${r.status ? ` (code ${r.status})` : ""}`} : {r.cause}</p>
          <p data-testid="admin-email-test-fix"><b>À faire :</b> {r.fix}</p>
          <p className="text-xs opacity-70">Clé : {r.key_present ? r.key_hint : "absente"}{r.key_present && !r.key_format_ok ? " · format suspect" : ""} · Expéditeur : {r.sender}</p>
          {r.detail && <p className="text-xs opacity-60 break-all">Réponse SendGrid : {r.detail}</p>}
        </div>
      )}
    </div>
  );
};

export const AdminOnboardingEmails = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/onboarding-emails").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const toggle = async () => { await api.put("/admin/onboarding-emails", { enabled: !d.enabled }); load(); };
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/onboarding-emails/run"); toast.success(`${r.data.sent} e-mail(s) envoyé(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6 space-y-3" data-testid="admin-onboarding-emails">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h3 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Mail className="w-4 h-4" /> E-mails d'accueil (J+0 → J+7)</h3>
        <button onClick={toggle} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${d.enabled ? "bg-brand-lime text-brand-greenDeep" : "bg-brand-softer text-brand-green/60"}`} data-testid="admin-emails-toggle">{d.enabled ? "Activés" : "Désactivés"}</button>
      </div>
      <ul className="text-sm text-brand-green/70 space-y-1">{d.sequence.map((s) => <li key={s.key}>J+{s.day} · {s.subject}</li>)}</ul>
      <p className="text-xs text-brand-green/50" data-testid="admin-emails-due">{d.due.length} e-mail(s) à envoyer maintenant (seulement si l'action n'est pas encore faite).</p>
      <button onClick={run} disabled={!d.enabled || busy || !d.due.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft text-sm disabled:opacity-40" data-testid="admin-emails-run">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer maintenant</button>
      <CatchupBox mode="week" title="Rattrapage : inscrits la semaine dernière sans e-mail de bienvenue" onDone={load} />
      <CatchupBox mode="noproperty" title="Rattrapage : tous les utilisateurs sans bien, jamais accueillis" onDone={load} />
    </div>
  );
};

const CatchupBox = ({ mode, title, onDone }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/onboarding-emails/catchup", { params: { mode } }).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!c) return null;
  const launch = async () => {
    if (!window.confirm(`Lancer le rattrapage pour ${c.candidates.length} utilisateur(s) ? L'e-mail de bienvenue part maintenant.`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/admin/onboarding-emails/catchup", null, { params: { mode } })).data;
      if (r.failed) toast.error(`${r.sent} envoyé(s), ${r.failed} en échec : utilisez « Tester l'envoi d'e-mail » pour voir la cause. Les échecs seront retentés demain.`);
      else toast.success(`Rattrapage lancé : ${r.sent} e-mail(s) de bienvenue envoyé(s)`);
      load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-xl bg-brand-softer p-4 space-y-2" data-testid={`admin-emails-catchup-${mode}`}>
      <p className="text-sm font-semibold text-brand-greenDeep">{title}</p>
      <ul className="text-xs text-brand-green/70 list-disc pl-4">{c.plan.map((p) => <li key={p}>{p}</li>)}</ul>
      <p className="text-xs text-brand-green/60" data-testid={`admin-catchup-count-${mode}`}>{c.candidates.length} utilisateur(s) concerné(s){c.running ? ` · ${c.running} déjà en rattrapage` : ""}</p>
      {c.candidates.length > 0 && <p className="text-xs text-brand-green/50 break-words">{c.candidates.slice(0, 8).map((u) => `${u.email} (${u.created_at})`).join(", ")}{c.candidates.length > 8 ? "…" : ""}</p>}
      <button onClick={launch} disabled={busy || !c.candidates.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-40" data-testid={`admin-catchup-launch-${mode}`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Lancer le rattrapage</button>
      <p className="text-[11px] text-brand-green/50">Les e-mails suivants partent automatiquement chaque jour à 9 h (UTC), même si la séquence normale est désactivée.</p>
    </div>
  );
};
